<template>
  <div class="DateInput" :class="{ 'is-error': hasError, 'is-disabled': disabled, 'is-static': static }">
    <FieldLabel
      v-if="label"
      :required="isRequired"
      :disabled
      :hideAsterisk
      :for="`${name}-${formatParts[0]}`"
    >
      {{ label }}
    </FieldLabel>

    <div class="DateInput-fields">
      <template v-for="part of formatParts" :key="part">
        <Dropdown
          v-if="part === 'DD'"
          v-model="day"
          :name="`${name}-DD`"
          :autocomplete="autocomplete ? `${autocomplete}-day` : undefined"
          :static="static"
          :placeholder="dayPlaceholder"
          :options="dayOptions"
          :disabled="disabled"
          :error="hasError"
        />
        <Dropdown
          v-else-if="part === 'MM'"
          v-model="month"
          :name="`${name}-MM`"
          :autocomplete="autocomplete ? `${autocomplete}-month` : undefined"
          :static="static"
          :placeholder="monthPlaceholder"
          :options="monthOptions"
          :disabled="disabled"
          :error="hasError"
        />
        <Dropdown
          v-else-if="part === 'YYYY'"
          v-model="year"
          :name="`${name}-YYYY`"
          :autocomplete="autocomplete ? `${autocomplete}-year` : undefined"
          :static="static"
          :placeholder="yearPlaceholder"
          :options="yearOptions"
          :disabled="disabled"
          :error="hasError"
        />
      </template>
    </div>

    <FieldHelper :errorMessage="errorMessage" :helperText="helperText" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { getDaysInMonth } from 'date-fns';
import { useFieldValidation, type FieldProps } from '@cvo/ui/composables';
import type { DropdownOption } from '@cvo/ui';
import { getIsoDate } from '@cvo/domain/utils/dateUtils';
import FieldLabel from '../../atoms/FieldLabel/FieldLabel.vue';
import Dropdown from '../Dropdown/Dropdown.vue';
import FieldHelper from '../../atoms/FieldHelper/FieldHelper.vue';

export type YearOrder = 'asc' | 'desc';

export interface DateInputProps extends FieldProps {
  format?: string;
  dayPlaceholder?: string;
  monthPlaceholder?: string;
  yearPlaceholder?: string;
  minYear?: number;
  maxYear?: number;
  yearRange?: number;
  yearOrder?: YearOrder;
  autocomplete?: string;
}

const props = withDefaults(defineProps<DateInputProps>(), {
  format: 'DD-MM-YYYY',
  dayPlaceholder: 'DD',
  monthPlaceholder: 'MM',
  yearPlaceholder: 'YYYY',
  yearRange: 100,
  yearOrder: 'desc',
  required: false,
  disabled: false,
  hideAsterisk: false,
});

const modelValue = defineModel<string | null>({ default: null });

const day = ref('');
const month = ref('');
const year = ref('');

const { hasError, errorMessage, isRequired } = useFieldValidation(props);

function initialize(val?: string | null) {
  if (!val) {
    day.value = '';
    month.value = '';
    year.value = '';
    return;
  }

  const [yearPart, monthPart, dayPart] = val.split('-');
  if (yearPart && monthPart && dayPart !== undefined) {
    year.value = yearPart;
    month.value = String(Number(monthPart));
    day.value = dayPart ? String(Number(dayPart)) : '';
  }
}

// Watch for external changes to modelValue (e.g., from form reset)
watch(modelValue, (newValue) => {
  // Only initialize if the new value is different from current internal state
  const currentIso = year.value && month.value && day.value
    ? getIsoDate(new Date(Number(year.value), Number(month.value) - 1, Number(day.value)))
    : null;

  if (newValue !== currentIso) {
    initialize(newValue);
  }
}, { immediate: true });


const formatParts = computed(() => {
  // Normalize and extract tokens like DD, MM, YYYY
  return props.format
    .toUpperCase()
    .match(/(DD|MM|YYYY)/g) || ['DD', 'MM', 'YYYY'];
});

const maxDaysInMonth = computed(() => {
  const resolvedYear = year.value ? Number(year.value) : new Date().getFullYear();
  return getDaysInMonth(new Date(resolvedYear, Number(month.value) - 1));
});

const MAX_DAYS_IN_MONTH = 31;
const DATES_PADDING = 2; // For leading zeros
const dayOptions = computed<DropdownOption[]>(() => {
  const totalDays = month.value ? maxDaysInMonth.value : MAX_DAYS_IN_MONTH;

  return Array.from({ length: totalDays }, (_unused, index) => {
    const val = String(index + 1);
    return { value: val, label: val.padStart(DATES_PADDING, '0') };
  });
});

// Month options (1-12)
const monthOptions = computed<DropdownOption[]>(() =>
  Array.from({ length: 12 }, (_unused, index) => {
    const val = String(index + 1);
    return { value: val, label: val.padStart(DATES_PADDING, '0') };
  }));

// Year options
const yearOptions = computed<DropdownOption[]>(() => {
  const currentYear = new Date().getFullYear();
  const startYear = props.minYear ?? currentYear - props.yearRange + 1;
  const endYear = props.maxYear ?? currentYear;
  const years = Array.from({ length: endYear - startYear + 1 }, (_unused, i) => endYear - i);

  if (props.yearOrder === 'asc') {
    years.reverse();
  }

  return years.map((_year) => ({ value: String(_year), label: String(_year) }));
});

// Reset day when it becomes invalid after month/year change (e.g. day 31 after switching to February).
watch([month, year], () => {
  if (!day.value || !month.value) return;
  if (Number(day.value) > maxDaysInMonth.value) {
    day.value = '';
  }
});

watch([day, month, year], function () {
  const _day = formatParts.value.includes('DD') ? day.value : '01';
  const _month = formatParts.value.includes('MM') ? month.value : '01';

  if (!_day || !_month || !year.value) {
    modelValue.value = null;
    return;
  }

  const iso = getIsoDate(new Date(Number(year.value), Number(_month) - 1, Number(_day)));
  modelValue.value = iso;
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .DateInput {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-2;

    // TODO CVO-18267: Replace with generic field label tokens once updated in Figma
    --field-label-color: #{vars.$atoms-dropdown-baseinput-enabled-label-color};

    &.is-error {
      --field-label-color: #{vars.$atoms-dropdown-baseinput-error-label-color};
    }

    &.is-disabled {
      --field-label-color: #{vars.$atoms-dropdown-baseinput-disabled-label-color};
    }

    &.is-static {
      --field-label-color: #{vars.$atoms-dropdown-baseinput-static-label-color};
    }
  }

  .DateInput-fields {
    display: grid;
    gap: vars.$dimensions-d-3;
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  }
}
</style>
